/*
 * KLAIR: Schutz gegen "keine gültigen ID- oder Zeilenparameter" im Cart Drawer
 * (Shopify Cart API antwortet mit 400/422 "Cannot find valid ID or line parameters")
 *
 * Ursache
 * -------
 * 1. Shopify hängt an jeden Line-Key einen Hash: `<variant_id>:<hash>`. Dieser Hash
 *    ändert sich, sobald sich die Rabatt-Allokation der Zeile ändert. In einem
 *    Bundle-Warenkorb ("3 Geschmäcker inkludiert") rechnet Shopify die Rabatte beim
 *    Entfernen einer Position über ALLE Zeilen neu — dadurch bekommt jede übrig
 *    gebliebene Zeile einen neuen Key. Live gemessen:
 *
 *      vorher : 55128513741187:f82458f7…  Cranberry Twist   0.00 CHF
 *      nachher: 55128513741187:8658ea6c…  Cranberry Twist   6.90 CHF
 *
 * 2. `CartDrawer._onCartChanged` (theme.js) ersetzt das Innen-HTML des Drawers erst
 *    1250 ms nach der Antwort. In diesem Fenster zeigen alle "Entfernen"-Links noch
 *    die alten Keys. Ein Klick darin schickt einen Key, den es nicht mehr gibt.
 *
 * Belegt durch Sentry-Breadcrumbs aus Produktion (JAVASCRIPT-A5):
 *   23:05:34.763  Klick "Entfernen Extra Mint"
 *   23:05:35.364  POST /cart/change.js  -> 200
 *   23:05:35.437  Klick "Entfernen KLAIR Schwarz Metallic"   (73 ms später)
 *   23:05:35.673  POST /cart/change.js  -> 400
 *
 * 3. Zusätzlich feuert theme.js pro Klick sofort einen Request, ohne laufende
 *    abzuwarten. Trafen die Antworten out-of-order ein, gewann der ältere Cart-Stand
 *    das verzögerte Neurendern und belebte eine bereits gelöschte Zeile wieder.
 *
 * Fix
 * ---
 * a) Line-Key beim Klick frisch aus /cart.js auflösen (über die Variant-ID im
 *    Key-Präfix), statt dem gerenderten DOM zu vertrauen.
 * b) Ist die Zeile serverseitig nicht mehr vorhanden: keinen zum Scheitern
 *    verurteilten Request schicken, sondern den Drawer auffrischen — die Geisterzeile
 *    verschwindet, statt eine rote Fehlermeldung zu zeigen.
 * c) Cart-Mutationen serialisieren, damit Antworten nicht out-of-order eintreffen.
 * d) Doppelklick auf "Entfernen" verwerfen, solange dessen Request läuft.
 * e) Fehlgeschlagene Cart-Änderungen an Sentry melden (Tag cart_operation=change).
 * f) Sonderfall /cart: dort lädt theme.js nach JEDER Änderung die Seite neu
 *    (window.location.reload bei pageType === 'cart'). Das bricht den /cart.js-Request
 *    ab, mit dem a) den frischen Key holt. Bis 01.09.2026 fiel der Wächter dann auf den
 *    alten Key zurück und löste damit genau den 422 aus, den er verhindern soll — die
 *    Regression von JAVASCRIPT-A5. Jetzt: einmal nachfassen, und wenn der Warenkorb
 *    weiterhin unlesbar ist, auf /cart gar nichts schicken. Im Drawer gibt es kein
 *    Reload, dort bleibt der alte Fallback. Ebenso hört auf /cart niemand auf
 *    'cart:refresh' aus b) — dort wird stattdessen neu geladen.
 */

/*
 * Kassen-Tor
 * ----------
 * Alles, was vor dem Checkout noch passieren muss, haengt als Schritt an diesem Tor:
 * zuerst die Sperre bei offenen Warenkorb-Aenderungen (weiter unten), spaeter das
 * Upsell-Popup. Gilt fuer das Kassen-Formular im Drawer und auf /cart - beide schicken
 * ein Formular mit button[name="checkout"] ab. Express-Buttons laufen nicht hier durch.
 *
 * Ein Schritt ist pruefe({ formular, knopf }) und liefert:
 *   false    -> anhalten, der Checkout faellt aus
 *   Promise  -> das Tor haelt das Absenden an, wartet und schickt danach selbst ab
 *               (loest das Promise mit false auf, bleibt es beim Anhalten)
 *   sonst    -> weiter
 *
 * Solange alle Schritte synchron "weiter" sagen, fasst das Tor das Ereignis nicht an -
 * der Checkout laeuft exakt wie ohne Tor. Wirft ein Schritt oder scheitert sein
 * Promise, geht es trotzdem weiter: ein kaputtes Popup darf niemanden an der Kasse
 * aufhalten.
 *
 * Beim erneuten Absenden laufen alle Schritte noch einmal, ausser denen, die schon
 * gewartet haben. So greift die Sperre auch, wenn waehrenddessen eine Aenderung
 * angelaufen ist.
 */
(function () {
  if (window.KlairKassenTor) return;

  const KASSE_AUSWAHL = 'button[name="checkout"]';
  const schritte = [];
  // Schritte, die in diesem Anlauf schon gewartet haben - nur waehrend absenden() gesetzt.
  let erledigt = null;
  let laeuft = false;

  function melde(fehler, schritt) {
    if (window.Sentry && typeof window.Sentry.captureException === 'function') {
      window.Sentry.captureException(fehler, { tags: { kassen_tor: schritt.name } });
    }
  }

  function fuehreAus(schritt, kontext) {
    try {
      return schritt.pruefe(kontext);
    } catch (fehler) {
      melde(fehler, schritt);
      return undefined;
    }
  }

  // Der Drawer tauscht sein Innen-HTML nach jeder Warenkorb-Aenderung aus. Hat ein
  // Schritt den Warenkorb veraendert (Upsell angenommen), ist das Formular von vorhin
  // nicht mehr im Dokument - dann das nachgerenderte Gegenstueck nehmen.
  function aktuellesFormular(formular) {
    if (formular.isConnected) return formular;
    const imDrawer = !!formular.closest('cart-drawer') || formular.classList.contains('buy-buttons');
    const kandidaten = Array.prototype.slice.call(document.querySelectorAll('form'));
    return (
      kandidaten.find(function (f) {
        return f.querySelector(KASSE_AUSWAHL) && !!f.closest('cart-drawer') === imDrawer;
      }) || null
    );
  }

  function absenden(formular, gewartet) {
    const ziel = aktuellesFormular(formular);
    if (!ziel) {
      window.location.href =
        ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'checkout';
      return;
    }

    erledigt = gewartet;
    try {
      // Mit Knopf, damit checkout= mitgeht - ohne landet der POST auf /cart statt im
      // Checkout.
      if (typeof ziel.requestSubmit === 'function') {
        ziel.requestSubmit(ziel.querySelector(KASSE_AUSWAHL));
      } else {
        const feld = document.createElement('input');
        feld.type = 'hidden';
        feld.name = 'checkout';
        ziel.appendChild(feld);
        ziel.submit();
      }
    } finally {
      erledigt = null;
    }
  }

  document.addEventListener(
    'submit',
    function (ereignis) {
      const formular = ereignis.target;
      if (!formular || !formular.querySelector || !formular.querySelector(KASSE_AUSWAHL)) return;

      const knopf = ereignis.submitter || null;
      if (knopf && !knopf.matches(KASSE_AUSWAHL)) return;

      const anhalten = function () {
        ereignis.preventDefault();
        ereignis.stopImmediatePropagation();
      };

      // Ein zweiter Klick, waehrend ein Schritt noch wartet.
      if (laeuft && !erledigt) return anhalten();

      const kontext = { formular: formular, knopf: knopf };
      const gewartet = erledigt || new Set();

      for (let i = 0; i < schritte.length; i++) {
        if (gewartet.has(schritte[i])) continue;

        const ergebnis = fuehreAus(schritte[i], kontext);

        if (ergebnis === false) return anhalten();

        if (ergebnis && typeof ergebnis.then === 'function') {
          anhalten();
          laeuft = true;
          const schritt = schritte[i];
          ergebnis
            .then(null, function (fehler) {
              melde(fehler, schritt);
            })
            .then(function (weiter) {
              laeuft = false;
              gewartet.add(schritt);
              // Die folgenden Schritte befragt erst der neue Durchlauf - der Warenkorb
              // kann sich inzwischen geaendert haben.
              if (weiter !== false) absenden(formular, gewartet);
            });
          return;
        }
      }
    },
    true
  );

  window.KlairKassenTor = {
    auswahl: KASSE_AUSWAHL,
    /** Haengt einen Schritt ein. Kleinere reihenfolge laeuft zuerst. */
    schritt: function (name, reihenfolge, pruefe) {
      schritte.push({ name: name, reihenfolge: reihenfolge, pruefe: pruefe });
      schritte.sort(function (a, b) {
        return a.reihenfolge - b.reihenfolge;
      });
    }
  };
})();

/*
 * Lade-Ring halten, bis der Warenkorb-Drawer offen ist.
 *
 * Alle Kauf-Knoepfe nehmen aria-busy weg, sobald sie den Drawer angestossen haben. Der faehrt
 * danach aber noch rund 0.6 s herein - in der Zeit stand wieder "In den Warenkorb" auf dem
 * Knopf. Statt jede Seite einzeln warten zu lassen, merkt sich dieser Block beim Oeffnen die
 * gerade ladenden Knoepfe und markiert sie mit .klair-haelt (Stil in theme.css), bis der
 * Drawer 'dialog:after-show' meldet.
 *
 * Angestossen wird der Drawer auf zwei Wegen, beide feuern noch vor dem Zuruecksetzen:
 * 'variant:add' (Theme-Formular, Paket-Auswahl) und 'cart:refresh' (Aromen-Auswahl und
 * Bestellseiten).
 */
(function () {
  const LADEND = ':is(.button, [is="custom-button"], .klair-laedt)[aria-busy="true"]';
  let gehalten = [];
  let uhr = null;

  function loese() {
    gehalten.forEach(function (knopf) {
      knopf.classList.remove('klair-haelt');
    });
    gehalten = [];
    clearTimeout(uhr);
  }

  function halte() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || drawer.hasAttribute('open')) return;

    document.querySelectorAll(LADEND).forEach(function (knopf) {
      if (gehalten.indexOf(knopf) !== -1) return;
      knopf.classList.add('klair-haelt');
      gehalten.push(knopf);
    });
    if (!gehalten.length) return;

    // Oeffnet sich der Drawer nicht (Fehler, anderer Warenkorb-Typ), gibt die Uhr frei.
    clearTimeout(uhr);
    uhr = setTimeout(loese, 1500);
  }

  document.addEventListener('variant:add', halte, true);
  document.addEventListener('cart:refresh', halte, true);

  // Ausnahme: Das Produktformular von theme.js (ProductForm._onSubmit) nimmt aria-busy schon
  // nach der Add-Antwort weg - vor dem Nachladen von /cart.js und damit vor 'variant:add'.
  // Dort also beim Wegnehmen selbst halten. Der Beobachter meldet sich noch vor dem
  // naechsten Zeichnen, der Text blitzt nicht auf. Bei einem Fehler oeffnet sich kein
  // Drawer, dann gibt 'cart:error' frei.
  function halteFormular(eintraege) {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || drawer.hasAttribute('open')) return;

    let neu = false;
    eintraege.forEach(function (eintrag) {
      const knopf = eintrag.target;
      if (knopf.getAttribute('aria-busy') === 'true' || eintrag.oldValue !== 'true') return;
      // Das Formular ist <form is="product-form"> - closest('product-form') trifft es nicht.
      if (knopf.type !== 'submit' || !knopf.form || knopf.form.getAttribute('is') !== 'product-form') return;
      if (gehalten.indexOf(knopf) !== -1) return;
      knopf.classList.add('klair-haelt');
      gehalten.push(knopf);
      neu = true;
    });
    if (!neu) return;

    clearTimeout(uhr);
    uhr = setTimeout(loese, 2500);
  }

  new MutationObserver(halteFormular).observe(document.documentElement, {
    subtree: true,
    attributes: true,
    attributeFilter: ['aria-busy'],
    attributeOldValue: true
  });
  document.addEventListener('cart:error', loese, true);
  document.addEventListener('dialog:after-show', function (event) {
    if (event.target instanceof Element && event.target.matches('cart-drawer')) loese();
  });
})();

(async function () {
  await customElements.whenDefined('line-item-quantity');

  const prototype = customElements.get('line-item-quantity').prototype;

  if (prototype.__klairCartGuardApplied) return;
  prototype.__klairCartGuardApplied = true;

  const original = prototype._changeLineItemQuantity;
  const inFlight = new Set();
  let queue = Promise.resolve();

  /*
   * Doppelte Animation verhindern
   * -----------------------------
   * LineItem._onChanged in theme.js klappt die Zeile beim Entfernen ebenfalls zusammen.
   * Wir starten dieselbe Bewegung schon beim Klick; laeuft die zweite kurz darauf an,
   * ueberlagern sich zwei Animationen mit unterschiedlichem Fortschritt auf denselben
   * Eigenschaften - sichtbar als Ruckeln.
   *
   * Fuer Zeilen, die wir bereits selbst klappen, entfaellt die Animation von theme.js
   * daher. Das Aufraeumen des Ladeindikators bleibt, und das Entfernen aus dem DOM
   * uebernimmt ohnehin blendeZeileAus().
   */
  /*
   * Zwischenstaende beim schnellen Entfernen unterdruecken
   * ------------------------------------------------------
   * Jede Antwort auf /cart/change.js loest ein Neurendern aus, und zwar mit dem HTML,
   * das der Server zu DIESEM Zeitpunkt kennt. Entfernt jemand drei Positionen rasch
   * hintereinander, enthaelt die Antwort des ersten Requests die beiden anderen noch -
   * sie tauchen kurz wieder auf und verschwinden erst mit der naechsten Antwort.
   *
   * Die Requests laufen ohnehin serialisiert. Solange noch weitere in der Warteschlange
   * stehen, ist die optimistische Anzeige bereits richtig und der Zwischenstand vom
   * Server veraltet - gerendert wird deshalb erst die letzte Antwort.
   */
  let ausstehend = 0;

  window.KlairCartWaechter = {
    ausstehend: function () {
      return ausstehend;
    }
  };

  /*
   * "Zur Kasse" sperren, solange eine Aenderung offen ist
   * ----------------------------------------------------
   * Das Entfernen ist optimistisch: die Zeile verschwindet sofort, der Request an
   * Shopify laeuft noch rund 840 ms. Wer in diesem Fenster auf "Zur Kasse" klickt,
   * nimmt die eben entfernte Position mit in den Checkout - serverseitig liegt sie
   * ja noch im Warenkorb.
   *
   * Die Schaltflaeche haengt darum am selben Zaehler, der die Requests verwaltet.
   * aria-busy ist die theme-eigene Ladeanzeige: CustomButton beobachtet das Attribut
   * und tauscht die Beschriftung gegen die drei laufenden Punkte. disabled verhindert
   * den Klick.
   *
   * Drei Dinge, die nicht auf der Hand liegen:
   *
   *   - Der Drawer tauscht sein Innen-HTML nach der Antwort aus. Eine frisch
   *     gerenderte Schaltflaeche waere wieder offen, obwohl noch etwas laeuft.
   *     Dagegen ein MutationObserver - aber nur, solange gesperrt ist.
   *   - Implizites Absenden per Enter umgeht eine deaktivierte Schaltflaeche: das
   *     Formular geht dann ohne name/value raus. Darum zusaetzlich als erster
   *     Schritt am Kassen-Tor (oben).
   *   - Bliebe ein Request haengen, waere die Kasse fuer immer zu. Nach 8 s gibt die
   *     Notbremse sie frei. Der Zaehler bleibt unangetastet, er steuert auch das
   *     Neurendern - nur die Sperre faellt.
   */
  const KASSE_AUSWAHL = window.KlairKassenTor.auswahl;
  let kasseGesperrt = false;
  let kasseBeobachter = null;
  let kasseUhr = null;

  function kasseWurzeln() {
    const gefunden = Array.prototype.slice.call(
      document.querySelectorAll('cart-drawer, .cart-form')
    );
    return gefunden.length ? gefunden : [document.body];
  }

  function kasseZustandAnwenden() {
    document.querySelectorAll(KASSE_AUSWAHL).forEach(function (knopf) {
      if (knopf.disabled !== kasseGesperrt) knopf.disabled = kasseGesperrt;
      // Nur schreiben, wenn sich etwas aendert - sonst laesst CustomButton bei jedem
      // Aufruf seine Ein-/Ausblend-Animation laufen.
      const istBeschaeftigt = knopf.getAttribute('aria-busy') === 'true';
      if (istBeschaeftigt !== kasseGesperrt) {
        knopf.setAttribute('aria-busy', kasseGesperrt ? 'true' : 'false');
      }
    });
  }

  function kasseSperren(sperren) {
    if (sperren === kasseGesperrt) return;
    kasseGesperrt = sperren;
    kasseZustandAnwenden();

    if (sperren) {
      kasseBeobachter = new MutationObserver(kasseZustandAnwenden);
      // Nur die Bereiche beobachten, in denen die Schaltflaeche vorkommt. An
      // document.body haengend wuerde der Rueckruf auf dieser Seite staendig durch
      // fremde Skripte ausgeloest, die mit dem Warenkorb nichts zu tun haben.
      kasseWurzeln().forEach(function (wurzel) {
        kasseBeobachter.observe(wurzel, { childList: true, subtree: true });
      });
      kasseUhr = setTimeout(function () {
        kasseSperren(false);
      }, 8000);
      return;
    }

    if (kasseBeobachter) {
      kasseBeobachter.disconnect();
      kasseBeobachter = null;
    }
    if (kasseUhr) {
      clearTimeout(kasseUhr);
      kasseUhr = null;
    }
  }

  // Einziger Weg, den Zaehler zu veraendern. So kann die Sperre nicht vergessen werden,
  // wenn spaeter eine weitere Stelle hoch- oder runterzaehlt.
  function setzeAusstehend(neu) {
    ausstehend = Math.max(0, neu);
    kasseSperren(ausstehend > 0);
  }

  window.KlairKassenTor.schritt('sperre', 10, function () {
    return kasseGesperrt ? false : undefined;
  });

  await customElements.whenDefined('cart-drawer');

  const drawerPrototyp = customElements.get('cart-drawer').prototype;

  if (!drawerPrototyp.__klairRenderSchutz) {
    drawerPrototyp.__klairRenderSchutz = true;

    const originalOnCartChanged = drawerPrototyp._onCartChanged;

    drawerPrototyp._onCartChanged = function (event) {
      if (ausstehend > 1) return;
      return originalOnCartChanged.call(this, event);
    };

    // Der Patch am Prototyp allein reicht hier nicht. CartDrawer bindet seinen Listener im
    // KONSTRUKTOR:
    //
    //   this._onCartChangedListener = this._onCartChanged.bind(this);
    //
    // bind() friert die Methode zum Zeitpunkt des Aufrufs ein. Die Drawer-Instanz existiert
    // laengst, wenn dieses Skript laeuft - sie zeigt also weiter auf die urspruengliche
    // Methode, egal was am Prototyp danach passiert. Genau deshalb blieb der erste Anlauf
    // wirkungslos, waehrend derselbe Patch bei LineItem sofort griff: das bindet erst in
    // connectedCallback, und Zeilen werden bei jedem Rendern neu erzeugt.
    //
    // Bestehende Instanzen bekommen ihren Listener daher neu gebunden.
    document.querySelectorAll('cart-drawer').forEach(function (drawer) {
      if (typeof drawer._onCartChangedListener !== 'function') return;

      const warVerbunden = drawer.isConnected;
      if (warVerbunden) document.removeEventListener('cart:change', drawer._onCartChangedListener);

      drawer._onCartChangedListener = drawer._onCartChanged.bind(drawer);

      if (warVerbunden) document.addEventListener('cart:change', drawer._onCartChangedListener);
    });
  }

  await customElements.whenDefined('line-item');

  const zeilenPrototyp = customElements.get('line-item').prototype;

  if (!zeilenPrototyp.__klairKlappSchutz) {
    zeilenPrototyp.__klairKlappSchutz = true;

    const originalOnChanged = zeilenPrototyp._onChanged;

    zeilenPrototyp._onChanged = function (event) {
      if (this.__klairKlapptZu) {
        if (this.pillLoaderElement) this.pillLoaderElement.removeAttribute('aria-busy');
        return;
      }
      return originalOnChanged.call(this, event);
    };
  }

  function wurzel() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function aufCartSeite() {
    return (
      (window.themeVariables &&
        window.themeVariables.settings &&
        window.themeVariables.settings.pageType) === 'cart'
    );
  }

  /*
   * Zwischenspeicher fuer den zuletzt bekannten Warenkorb.
   *
   * Jede Antwort auf /cart/change.js enthaelt den vollstaendigen Warenkorb, und theme.js
   * reicht ihn als 'cart:change' weiter. Damit kennen wir die frischen Line-Keys bereits,
   * bevor der Drawer sein HTML austauscht — und muessen sie nicht per /cart.js nachladen.
   * Das war bisher ein zusaetzlicher Roundtrip von rund 180 ms vor JEDEM Klick.
   */
  let letzterWarenkorb = null;

  document.addEventListener('cart:change', function (event) {
    if (event.detail && event.detail.cart && Array.isArray(event.detail.cart.items)) {
      letzterWarenkorb = event.detail.cart;
    }
  });

  // Nach einem Refresh wissen wir nicht mehr, was drin ist - lieber wieder nachlesen.
  document.addEventListener('cart:refresh', function () {
    letzterWarenkorb = null;
  });

  /**
   * Wie aktuellerLineKey(), aber ohne Netz: loest gegen den Zwischenspeicher auf.
   * undefined = kein Zwischenspeicher vorhanden, Aufrufer muss selbst entscheiden.
   */
  function keyAusZwischenspeicher(veralteterKey) {
    if (!letzterWarenkorb) return undefined;

    const variantId = String(veralteterKey).split(':')[0];
    const treffer = letzterWarenkorb.items.filter(function (item) {
      return String(item.key).split(':')[0] === variantId;
    });

    if (treffer.length === 0) return null;

    const exakt = treffer.find(function (item) {
      return item.key === veralteterKey;
    });
    if (exakt) return exakt.key;

    if (treffer.length > 1) return null;

    return treffer[0].key;
  }

  /**
   * Liefert den aktuell gültigen Line-Key zu einem (möglicherweise veralteten) Key.
   * null = die Zeile existiert serverseitig nicht mehr.
   * undefined = Warenkorb nicht lesbar, Aufrufer soll den Original-Key verwenden.
   */
  async function aktuellerLineKey(veralteterKey) {
    const variantId = String(veralteterKey).split(':')[0];
    if (!variantId) return undefined;

    let cart;
    try {
      const response = await fetch(wurzel() + 'cart.js', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      });
      if (!response.ok) return undefined;
      cart = await response.json();
    } catch (error) {
      return undefined;
    }

    if (!cart || !Array.isArray(cart.items)) return undefined;

    const treffer = cart.items.filter(function (item) {
      return String(item.key).split(':')[0] === variantId;
    });

    if (treffer.length === 0) return null;

    // Exakter Key noch vorhanden -> nichts umzubiegen.
    const exakt = treffer.find(function (item) {
      return item.key === veralteterKey;
    });
    if (exakt) return exakt.key;

    // Mehrere Zeilen derselben Variante (unterschiedliche Properties): ohne exakten
    // Treffer ist nicht sicher entscheidbar, welche gemeint war — dann lieber den
    // Drawer auffrischen als die falsche Zeile zu entfernen.
    if (treffer.length > 1) return null;

    return treffer[0].key;
  }

  /*
   * Optimistisches Entfernen
   * ------------------------
   * Der Request an Shopify dauert rund 840 ms, daran koennen wir nichts aendern. Bis die
   * Zeile verschwand, verging deshalb fast eine Sekunde - das Bedienen fuehlte sich zaeh an.
   *
   * Also andersherum: die Zeile beim Klick sofort ausblenden und den Request im Hintergrund
   * laufen lassen. Kommt er durch, ersetzt der Drawer ohnehin seinen Inhalt. Scheitert er,
   * klappt die Zeile wieder auf und theme.js zeigt seine Meldung.
   *
   * Wichtig: Das passiert VOR der Warteschlange. Sonst wuerde beim schnellen Entfernen
   * mehrerer Positionen die zweite Zeile erst verschwinden, wenn die erste fertig ist -
   * genau das Warten, das wir loswerden wollen.
   */
  function blendeZeileAus(zeile) {
    if (!zeile) return null;

    // Die Zeile klappt in 200 ms zusammen und wird danach aus dem DOM genommen.
    //
    // Bewusst ueber die Web Animations API und mit denselben Keyframes wie theme.js.
    // Zwei Anlaeufe davor sind daran gescheitert, das anders loesen zu wollen:
    //
    //   1. Eine eigene WAAPI-Animation neben der von theme.js - beide liefen, das Ergebnis
    //      war unklar, weil ich am falschen Element gemessen hatte.
    //   2. Eine CSS-Transition ueber Inline-Styles - chancenlos: WAAPI-Animationen
    //      ueberstimmen Transitions. Live gemessen liefen zwei Transitions korrekt, waehrend
    //      die berechnete Hoehe unveraendert stehen blieb, weil theme.js dieselbe
    //      Eigenschaft per WAAPI animierte.
    //
    // theme.js macht bei 'line-item:change' exakt diese Bewegung (LineItem._onChanged).
    // Wir starten sie nur frueher - beim Klick statt nach der Serverantwort. Laeuft die
    // Animation von theme.js spaeter zusaetzlich an, hat sie dieselben Zielwerte, das
    // Ergebnis bleibt gleich.
    //
    // Das negative marginBottom ist die Kompensation fuer
    //   .cart-drawer__line-items > * + * { padding-block-start: var(--spacing-4); }
    // Ohne sie bliebe am Ende der Bewegung ein 16px-Sprung stehen.
    const elternteil = zeile.parentNode;
    const nachbar = zeile.nextSibling;
    const naechste = zeile.nextElementSibling;
    const ausgleich = naechste ? '-' + getComputedStyle(naechste).paddingBlockStart : '0px';
    const hoehe = zeile.getBoundingClientRect().height;

    zeile.style.overflow = 'hidden';
    zeile.__klairKlapptZu = true;

    let animation = null;
    if (typeof zeile.animate === 'function') {
      animation = zeile.animate(
        [
          { height: hoehe + 'px', marginBottom: '0px', opacity: 1 },
          { height: '0px', marginBottom: ausgleich, opacity: 0 }
        ],
        { duration: 200, easing: 'ease', fill: 'forwards' }
      );
    }

    let entfernt = false;
    const wegNehmen = function () {
      entfernt = true;
      zeile.remove();
    };
    const uhr = setTimeout(wegNehmen, 210);

    return function wiederherstellen() {
      clearTimeout(uhr);
      if (animation) animation.cancel();
      zeile.style.overflow = '';
      zeile.__klairKlapptZu = false;
      if (entfernt && elternteil) elternteil.insertBefore(zeile, nachbar);
    };
  }

  /*
   * Zaehler und Versandleiste mitziehen
   * -----------------------------------
   * Beide haengen an 'cart:change' und warten damit auf die Serverantwort - die Zeile war
   * laengst weg, oben stand aber noch die alte Artikelzahl. Wir kennen die entfernte
   * Position aus dem Zwischenspeicher und koennen beides sofort setzen.
   *
   * Bei der Versandleiste ist das eine Schaetzung: faellt eine Position weg, verteilt
   * Shopify die Rabatte ueber die uebrigen Zeilen neu, und wie das ausgeht, weiss nur der
   * Server. Der Abzug geht aber immer nach UNTEN - die Leiste behauptet also nie
   * faelschlich, der Versand sei bereits kostenlos. Die Serverantwort korrigiert kurz
   * darauf ohnehin. Gratis-Zeilen haben final_line_price 0 und aendern gar nichts.
   */
  function zieheAnzeigenVor(lineKey, zeile) {
    const aktueller = keyAusZwischenspeicher(lineKey);
    const posten =
      letzterWarenkorb && typeof aktueller === 'string'
        ? letzterWarenkorb.items.find(function (item) {
            return item.key === aktueller;
          })
        : null;

    let menge = posten ? posten.quantity : 0;
    if (!menge && zeile) {
      const feld = zeile.querySelector('.quantity-input');
      menge = feld ? parseInt(feld.value, 10) || 0 : 0;
    }

    const zaehler = Array.prototype.slice.call(document.querySelectorAll('cart-count'));
    const alteZahlen = zaehler.map(function (el) {
      return el.itemCount;
    });
    if (menge) {
      zaehler.forEach(function (el) {
        el.itemCount = Math.max(0, el.itemCount - menge);
      });
    }

    const leiste = document.querySelector('free-shipping-bar');
    const alterBetrag = leiste ? leiste.totalPrice : null;
    const abzug = posten && posten.requires_shipping ? posten.final_line_price : 0;
    if (leiste && abzug) {
      leiste.totalPrice = Math.max(0, alterBetrag - abzug);
    }

    return function zurueck() {
      zaehler.forEach(function (el, i) {
        el.itemCount = alteZahlen[i];
      });
      if (leiste && alterBetrag !== null) leiste.totalPrice = alterBetrag;
    };
  }

  prototype._changeLineItemQuantity = function (lineKey, targetQuantity) {
    // Doppelklick auf "Entfernen": der zweite Klick würde einen Line-Key schicken,
    // den es nach dem ersten Request nicht mehr gibt.
    // Bewusst nur für quantity === 0: eine schnelle Mengenänderung auf derselben
    // Zeile darf nicht verschluckt werden, sonst laufen Anzeige und Warenkorb
    // auseinander.
    const istEntfernen = targetQuantity === 0;

    if (istEntfernen && inFlight.has(lineKey)) return queue;
    if (istEntfernen) inFlight.add(lineKey);

    // Sofort, noch bevor die Warteschlange laeuft: Zeile verschwindet, Zaehler und
    // Versandleiste ziehen mit.
    const zeile = istEntfernen ? this.closest('line-item, tr') : null;
    const zurueckZeile = istEntfernen ? blendeZeileAus(zeile) : null;
    const zurueckAnzeigen = istEntfernen ? zieheAnzeigenVor(lineKey, zeile) : null;
    // Mengenaenderung: Lade-Ring in der Pille der Zeile sofort zeigen. theme.js setzt ihn erst
    // in der Originalmethode, und die laeuft hinter der Warteschlange - bei der ersten
    // Aenderung nach dem Seitenaufbau also erst nach dem Nachlesen des Line-Keys.
    // Zurueckgesetzt wird er von LineItem (line-item:change / :error), ohne Wirkung unten.
    const eigeneZeile = istEntfernen ? null : this.closest('line-item');
    if (eigeneZeile) {
      eigeneZeile.dispatchEvent(
        new CustomEvent('line-item:will-change', { bubbles: true, detail: { targetQuantity: targetQuantity } })
      );
    }
    const wiederherstellen = function () {
      if (zurueckZeile) zurueckZeile();
      if (zurueckAnzeigen) zurueckAnzeigen();
    };

    // 'cart:change' feuert nur im Erfolgsfall. Auf der Cart-Seite gibt es kein
    // 'line-item:error' (theme.js verschickt das nur ueber ein <line-item>-Element,
    // und dort sind die Zeilen <tr>), deshalb dieses Signal statt des Fehler-Events.
    setzeAusstehend(ausstehend + 1);

    let erfolgreich = false;
    const merkeErfolg = () => {
      erfolgreich = true;
    };
    document.addEventListener('cart:change', merkeErfolg, { once: true });

    // Womit der Request am Ende rausging - fuer die Fehlermeldung unten.
    const spur = { quelle: null, gesendet: null, domKey: lineKey };

    queue = queue
      .then(async () => {
        // Zuerst der Zwischenspeicher - kostet nichts. Er kennt genau den Fall, fuer den
        // dieser Waechter gebaut wurde: die Key-Rotation nach unserer eigenen letzten
        // Aenderung. Nur ohne Zwischenspeicher (erste Aenderung nach dem Seitenaufbau)
        // wird noch nachgelesen; die im DOM gerenderten Keys stammen dann direkt vom
        // Server und sind gueltig.
        let aktuell = keyAusZwischenspeicher(lineKey);
        spur.quelle = aktuell === undefined ? null : 'zwischenspeicher';

        if (aktuell === undefined) {
          aktuell = await aktuellerLineKey(lineKey);
          spur.quelle = 'netz';
        }

        // Warenkorb nicht lesbar: einmal kurz nachfassen. Deckt kurze Netz-Aussetzer ab.
        if (aktuell === undefined) {
          await new Promise((fertig) => setTimeout(fertig, 150));
          aktuell = await aktuellerLineKey(lineKey);
        }

        if (aktuell === undefined) {
          // Auf /cart laedt theme.js nach JEDER Aenderung die Seite neu
          // (window.location.reload bei pageType === 'cart'). Das bricht genau den
          // /cart.js-Request ab, mit dem wir den frischen Key holen wollten. Frueher
          // fiel der Waechter hier auf den ALTEN Key zurueck und schickte damit
          // ausgerechnet den Request, den er verhindern soll -> 422 (JAVASCRIPT-A5,
          // Regression vom 01.09.2026 auf /cart).
          //
          // Dort also lieber gar nichts schicken: ein verschluckter Klick waehrend
          // eines laufenden Reloads ist folgenlos, ein 422 mit roter Fehlermeldung
          // nicht. Ausserhalb von /cart gibt es kein Reload, ein unlesbarer Warenkorb
          // ist dann ein echter Netzwerkfehler - da bleibt es beim alten Verhalten.
          if (aufCartSeite()) return;
          spur.quelle = 'rueckfall-alter-key';
          spur.gesendet = lineKey;
          return original.call(this, lineKey, targetQuantity);
        }

        if (aktuell === null) {
          // Zeile ist serverseitig weg (oder nicht eindeutig): auffrischen statt eine
          // rote Fehlermeldung an einer Geisterzeile zu erzeugen. Auf /cart hoert
          // niemand auf 'cart:refresh' - dort ist der Drawer gar nicht gerendert,
          // der Klick waere sonst wirkungslos verpufft.
          if (aufCartSeite()) {
            window.location.reload();
          } else {
            document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          }
          return;
        }

        spur.gesendet = aktuell;

        try {
          await original.call(this, aktuell, targetQuantity);
        } catch (fehler) {
          // theme.js wirft im Fehlerfall selbst: es rendert die rote Meldung ueber
          // this.closest('.line-item, tr'), und nach dem optimistischen Entfernen ist die
          // Zeile aus dem DOM. Fuer uns ist das kein Abbruchgrund.
        }

        if (erfolgreich) return;

        // Zweiter Versuch mit frisch gelesenem Key.
        //
        // Belegt durch JAVASCRIPT-BT (iPhone, einzelner Klick, offene_operationen 0):
        // Zwischenspeicher UND DOM trugen denselben Key, und der Server lehnte ihn ab.
        // Beide waren also gegenueber dem Server veraltet. Der Line-Key enthaelt einen
        // Hash der Rabatt-Allokation; rechnet Shopify die serverseitig neu, verfallen alle
        // Keys, die der Browser haelt - ohne dass ein Ereignis davon erzaehlt. Dagegen
        // hilft kein Zwischenspeicher, nur Nachlesen.
        //
        // Der erste Versuch hat nichts veraendert (kein cart:change), ein zweiter ist also
        // gefahrlos. Fuer die Kundin bleibt es unsichtbar: die Zeile ist bereits
        // ausgeblendet, und theme.js konnte an der entfernten Zeile ohnehin nichts anzeigen.
        letzterWarenkorb = null;

        const frisch = await aktuellerLineKey(lineKey);
        if (typeof frisch !== 'string' || frisch === aktuell) return;

        spur.quelle = 'zweiter-versuch';
        spur.gesendet = frisch;

        try {
          await original.call(this, frisch, targetQuantity);
        } catch (fehler) {
          /* siehe oben */
        }
      })
      .catch((error) => {
        if (window.Sentry && typeof window.Sentry.captureException === 'function') {
          window.Sentry.captureException(error, { tags: { cart_operation: 'change' } });
        }
      })
      .then(() => {
        setzeAusstehend(ausstehend - 1);
        document.removeEventListener('cart:change', merkeErfolg);

        const pille = !erfolgreich && eigeneZeile && eigeneZeile.querySelector('pill-loader');
        if (pille) pille.removeAttribute('aria-busy');

        // Selbst melden statt auf 'line-item:error' zu warten. Dieses Event verschickt
        // theme.js ueber `this.closest("line-item")` - nach dem optimistischen Entfernen ist
        // die Zeile aber aus dem DOM, closest() liefert null, und theme.js wirft davor schon
        // einen TypeError. Fehlschlaege blieben dadurch unsichtbar. Hier haben wir ohnehin
        // den vollen Kontext: welcher Key rausging und woher er stammte.
        if (!erfolgreich && window.Sentry && typeof window.Sentry.captureMessage === 'function') {
          window.Sentry.captureMessage('Cart-Aenderung ohne Wirkung', {
            level: 'warning',
            tags: { cart_operation: 'change', schluessel_quelle: spur.quelle || 'unbekannt' },
            extra: {
              gesendeter_key: spur.gesendet,
              key_im_dom: spur.domKey,
              key_ersetzt: spur.gesendet !== spur.domKey,
              war_entfernen: istEntfernen,
              zeile_noch_im_dom: zeile ? zeile.isConnected : null,
              offene_operationen: ausstehend,
              zeilen_im_drawer: document.querySelectorAll('line-item').length
            }
          });
        }

        if (!istEntfernen) return;

        inFlight.delete(lineKey);

        // Kein 'cart:change' gesehen: der Warenkorb hat sich nicht geaendert - abgebrochen,
        // Netzwerkfehler oder von Shopify abgelehnt. Die Zeile muss zurueck, sonst zeigt der
        // Warenkorb weniger an, als er enthaelt.
        if (!erfolgreich) {
          wiederherstellen();
        }

      });

    return queue;
  };

  // Fehlgeschlagene Cart-Änderungen sichtbar machen: theme.js rendert bei !response.ok
  // nur die rote Meldung in die Zeile und wirft nichts, der Fehler bliebe sonst unsichtbar.
  document.addEventListener(
    'line-item:error',
    function (event) {
      if (!window.Sentry || typeof window.Sentry.captureMessage !== 'function') return;

      const row = event.target.closest('.line-item, tr');
      const alertElement = row && row.querySelector('[role="alert"]');

      window.Sentry.captureMessage('Cart-Änderung fehlgeschlagen', {
        level: 'warning',
        tags: { cart_operation: 'change' },
        extra: {
          meldung: alertElement ? alertElement.textContent.trim() : null,
          zeilen_im_drawer: document.querySelectorAll('line-item').length
        }
      });
    },
    true
  );
})();
